"use client";

import { HocuspocusProvider } from "@hocuspocus/provider";
import * as React from "react";
import { IndexeddbPersistence } from "y-indexeddb";
import { Doc as YDoc } from "yjs";
import { editorEnv } from "../env";

type CollabContextValue = {
  ydoc: YDoc | null;
  provider: HocuspocusProvider | null;
  localProvider: IndexeddbPersistence | null;

  // remote
  isSynced: boolean;
  error: { message: string } | null;

  // // local
  isSyncedLocal: boolean;
};

type CollabSyncContextValue = {
  isConnected: boolean;
  unsyncedChanges: number;
  lastSavedAt: Date | null;
};

const CollabContext = React.createContext<CollabContextValue>({
  ydoc: null,
  provider: null,
  localProvider: null,
  isSynced: false,
  isSyncedLocal: false,
  error: null,
});

const CollabSyncContext = React.createContext<CollabSyncContextValue>({
  isConnected: false,
  unsyncedChanges: 0,
  lastSavedAt: null,
});

export const CollabConsumer = CollabContext.Consumer;
export const CollabSyncConsumer = CollabSyncContext.Consumer;

export const useCollab = (): CollabContextValue => {
  const context = React.useContext(CollabContext);
  if (!context) {
    throw new Error("useCollab must be used within an CollabProvider");
  }
  return context;
};

export const useCollabSync = (): CollabSyncContextValue => {
  const context = React.useContext(CollabSyncContext);
  if (!context) {
    throw new Error("useCollabSync must be used within a CollabProvider");
  }
  return context;
};

export const useCollaboration = (props: {
  room: string;
  isRemoteSyncEnabled: boolean;
}) => {
  const [provider, setProvider] = React.useState<HocuspocusProvider | null>(
    null,
  );
  const [error, setError] = React.useState<{ message: string } | null>(null);
  const [isSynced, setIsSynced] = React.useState<boolean>(false);

  const [localProvider, setLocalProvider] =
    React.useState<IndexeddbPersistence | null>(null);
  const [isSyncedLocal, setIsSyncedLocal] = React.useState<boolean>(false);

  const ydoc = React.useMemo(() => new YDoc(), [props.room]);
  const documentName = props.room;

  React.useEffect(() => {
    const newLocalProvider = new IndexeddbPersistence(documentName, ydoc);

    setLocalProvider(newLocalProvider);

    return () => {
      newLocalProvider.destroy();
    };
  }, [ydoc]);

  React.useEffect(() => {
    if (!localProvider) return;
    const handleSynced = () => {
      setIsSyncedLocal(true);
    };
    localProvider.on("synced", handleSynced);
    return () => {
      localProvider.off("synced", handleSynced);
    };
  }, [localProvider]);

  React.useEffect(() => {
    if (props.isRemoteSyncEnabled === false) return;

    const newProvider = new HocuspocusProvider({
      name: documentName,
      url: editorEnv.NEXT_PUBLIC_COLLAB_SERVER_URL,
      document: ydoc,
      onAuthenticated: () => {
        setError(null);
      },
      onAuthenticationFailed: (data) => {
        console.error("Authentication failed:", data);
        setError({ message: "Authentication failed: " + data.reason });
      },
      onSynced: () => {
        setIsSynced(true);
      },
    });

    setProvider(newProvider);

    return () => {
      newProvider.destroy();
    };
  }, [ydoc, props.isRemoteSyncEnabled]);

  return { provider, ydoc, error, localProvider, isSynced, isSyncedLocal };
};

export function CollabProvider(
  props: Readonly<{
    children: React.ReactNode;
    room: string;
    isRemoteSyncEnabled: boolean;
  }>,
) {
  const value = useCollaboration({
    room: props.room,
    isRemoteSyncEnabled: props.isRemoteSyncEnabled,
  });

  return (
    <CollabContext.Provider value={value}>
      {props.children}
    </CollabContext.Provider>
  );
}

export function CollabSyncProvider({
  children,
  provider,
}: Readonly<{
  children: React.ReactNode;
  provider: HocuspocusProvider | null;
}>) {
  const [unsyncedChanges, setUnsyncedChanges] = React.useState<number>(0);
  const [isConnected, setIsConnected] = React.useState<boolean>(true);
  const [lastSavedAt, setLastSavedAt] = React.useState<Date | null>(null);

  React.useEffect(() => {
    if (!provider) return;

    const handleUnsyncedChanges = (unsyncedChanges: { number: number }) => {
      setUnsyncedChanges((prev) => {
        if (prev > 0 && unsyncedChanges.number === 0) {
          // just saved!
          setLastSavedAt(new Date());
        }

        return unsyncedChanges.number;
      });
    };
    const handleConnect = () => {
      setIsConnected(true);
    };
    const handleDisconnect = () => {
      setIsConnected(false);
    };

    provider.on("unsyncedChanges", handleUnsyncedChanges);
    provider.on("connect", handleConnect);
    provider.on("disconnect", handleDisconnect);

    return () => {
      provider.off("unsyncedChanges", handleUnsyncedChanges);
      provider.off("connect", handleConnect);
      provider.off("disconnect", handleDisconnect);
    };
  }, [provider]);

  return (
    <CollabSyncContext.Provider
      value={{ isConnected, unsyncedChanges, lastSavedAt }}
    >
      {children}
    </CollabSyncContext.Provider>
  );
}
